import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import useTyreStore from '../../store/useTyreStore';
import useCartStore from '../../store/useCartStore';

// Alerts
import * as alertify from '../../components/Alerts/lib/alertify';

// Components
import TyreHeader from './components/TyreHeader/TyreHeader';
import TyreMarketing from './components/TyreMarketing/TyreMarketing';
import TyreTechnical from './components/TyreTechnical/TyreTechnical';
import TyreSpecs from './components/TyreSpecs/TyreSpecs';
import TyreEULabel from './components/TyreEULabel/TyreEULabel';
import TyreMedia from './components/TyreMedia/TyreMedia';
import TyreImageGallery from './components/TyreImageGallery/TyreImageGallery';
import TyreSectionNav from './components/TyreSectionNav/TyreSectionNav';
import RelatedTyres from './components/RelatedTyres/RelatedTyres';
import CollapsibleSection from '../../components/CollapsibleSection/CollapsibleSection';
import Skeleton from '../../components/SkeletonLoader/SkeletonLoader';
import TyreStickyFooter from './components/TyreStickyFooter/TyreStickyFooter';

// Routes
import routeCartPage from '../../pages/CartPage/route';

// Styles
import {
  Container,
  SectionNavBar,
  TwoColumnLayout,
  ImageColumn,
  RelatedSection,
  ContentColumn,
  LoadingContainer,
  LoadingMessage,
  ErrorContainer,
  ContentSection,
} from './TyreContainer.style';

const TyreContainer = ({ ean }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const SECTIONS = [
    { id: 'header', label: t('overview') },
    { id: 'marketing', label: t('marketing') },
    { id: 'technical', label: t('technicalHighlights') },
    { id: 'specs', label: t('technicalSpecs') },
    { id: 'eulabel', label: t('euLabelInfo') },
    { id: 'media', label: t('mediaGallery') },
    { id: 'related', label: t('relatedProducts') },
  ];
  const observerRef = useRef(null);

  // Store state
  const {
    tyreData,
    priceData,
    loading,
    error,
    activeSection,
    setActiveSection,
    fetchTyreDetails,
    getPreparedData,
    reset,
  } = useTyreStore();

  const { addItem: addToCart } = useCartStore();

  // Setup intersection observer
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: null,
        rootMargin: '-100px 0px -50% 0px',
        threshold: 0,
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setActiveSection]);

  // Fetch tyre details on mount or EAN change
  useEffect(() => {
    fetchTyreDetails(ean);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [ean, fetchTyreDetails]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Observe section helper
  const observeSection = useCallback(
    (element) => {
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    },
    []
  );

  // Handle add to cart
  const handleAddToCart = useCallback(
    async (catNum, quantity, deliveryDate, scheduleDetails = [], isNotInStock = false, notInStockQuantity = null) => {
      if (!tyreData) return;

      try {
        const cartItem = {
          _id: tyreData.artNo11Digit || catNum,
          catalogNumber: tyreData.artNo11Digit || catNum,
          name: tyreData.productName,
          productName: tyreData.productName,
          brand: tyreData.singleBrandKey?.name,
          dimensionMetric: tyreData.dimensionMetric,
          season: tyreData.season,
          ean: tyreData.ean,
          cartQuantity: quantity,
          confirmedDeliveryDate: deliveryDate || null,
          scheduleDetails: scheduleDetails || [],
          isNotInStock,
          notInStockQuantity,
          grossPrice: priceData?.grossPrice || 0,
          netPrice: priceData?.netPrice || 0,
          discountGrossPrice: priceData?.discountGrossPrice || 0,
        };

        addToCart(cartItem);
        alertify.success(t('addedToCart'));
        history.push(routeCartPage());
      } catch (err) {
        console.error('Error adding to cart:', err);
        alertify.error(t('errorHappened'));
      }
    },
    [tyreData, priceData, addToCart, history, t]
  );

  // Prepare data for components
  const preparedData = getPreparedData();

  return (
    <Container>
      {loading && (
        <LoadingContainer>
          <LoadingMessage>
            <i className="mdi mdi-loading mdi-spin" />
            {t('loadingTyreDetails')}
          </LoadingMessage>
          <TwoColumnLayout>
            <ImageColumn>
              <Skeleton type="image" aspectRatio="1" radius="12px" />
            </ImageColumn>
            <ContentColumn>
              <Skeleton height="150px" radius="12px" margin="0 0 20px 0" />
              <Skeleton height="200px" radius="12px" margin="0 0 20px 0" />
              <Skeleton height="180px" radius="12px" margin="0 0 20px 0" />
            </ContentColumn>
          </TwoColumnLayout>
        </LoadingContainer>
      )}

      {error && (
        <ErrorContainer>
          <i
            className="mdi mdi-alert-circle"
            style={{ fontSize: '48px', marginBottom: '16px' }}
          />
          {t('errorLoadingTyreDetails')}: {error}
        </ErrorContainer>
      )}

      {!loading && !error && !tyreData && (
        <ErrorContainer>
          <i
            className="mdi mdi-alert"
            style={{ fontSize: '48px', marginBottom: '16px' }}
          />
          {t('noResults')}
        </ErrorContainer>
      )}

      {!loading && !error && tyreData && preparedData && (
        <>
          <SectionNavBar>
            <TyreSectionNav sections={SECTIONS} activeSection={activeSection} />
          </SectionNavBar>

          <TwoColumnLayout>
            <ImageColumn>
              <TyreImageGallery
                mainImage={tyreData.mainImage?.fullpath}
                media={preparedData.media}
              />

              <RelatedSection id="related" ref={observeSection}>
                <RelatedTyres
                  ean={tyreData.ean}
                  dimension={tyreData.dimensionMetric}
                  compact
                />
              </RelatedSection>
            </ImageColumn>

            <ContentColumn>
              <ContentSection id="header" ref={observeSection} delay="0s">
                <TyreHeader
                  productName={tyreData.productName}
                  brand={tyreData.singleBrandKey?.name}
                  catalogNumber={tyreData.artNo11Digit}
                  headline={tyreData.headline_approved}
                  season={tyreData.season}
                />
              </ContentSection>

              <CollapsibleSection
                id="marketing"
                ref={observeSection}
                title={
                  <TyreMarketing
                    marketingText={tyreData.marketingText}
                    introText={tyreData.introText_approved}
                    customerBenefits={preparedData.customerBenefits}
                    businessPartnerBenefits={preparedData.businessPartnerBenefits}
                  />
                }
                collapsible={false}
                storageKey="tyre_marketing"
              >
                <div />
              </CollapsibleSection>

              <CollapsibleSection
                id="technical"
                ref={observeSection}
                title={
                  <TyreTechnical
                    technicalHighlightsB2C={preparedData.technicalHighlightsB2C}
                    technicalHighlightsB2B={preparedData.technicalHighlightsB2B}
                  />
                }
                collapsible={false}
                storageKey="tyre_technical"
              >
                <div />
              </CollapsibleSection>

              <CollapsibleSection
                id="specs"
                ref={observeSection}
                title={t('technicalSpecs')}
                collapsible={true}
                storageKey="tyre_specs"
              >
                <div>
                  <TyreSpecs specs={preparedData.specs} />
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                id="eulabel"
                ref={observeSection}
                title={t('euLabelInfo')}
                collapsible={true}
                storageKey="tyre_eulabel"
              >
                <div>
                  <TyreEULabel euLabel={preparedData.euLabel} />
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                id="media"
                ref={observeSection}
                title={<TyreMedia media={preparedData.media} />}
                collapsible={false}
                storageKey="tyre_media"
              >
                <div />
              </CollapsibleSection>
            </ContentColumn>
          </TwoColumnLayout>
        </>
      )}

      {!loading && !error && tyreData && (
        <TyreStickyFooter
          productName={tyreData.productName}
          catalogNumber={tyreData.artNo11Digit}
          dimensions={tyreData.dimensionMetric}
          stock={4}
          grossPrice={priceData?.grossPrice}
          onAddToCart={handleAddToCart}
        />
      )}
    </Container>
  );
};

TyreContainer.propTypes = {
  ean: PropTypes.string.isRequired,
};

export default TyreContainer;
